import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import type { TranslationKey } from '@web-platform/shared-universal-constants/src/translationKeys';
import type { TranslationKeyOfGC } from '@web-platform/shared-universal-constants/src/translationKeysOfGlobalComponent';

let translationBody: Record<string, string> | undefined;
let hasTranslations = false;

/**
 * @internal Storybook/test-only. Clears the cached translations so the next translate() call re-reads from window.__internal.
 * @returns {void}
 */
export const resetTranslationCache = (): void => {
  translationBody = undefined;
  hasTranslations = false;
};

const getTranslations = () => {
  if (translationBody) return translationBody;

  // eslint-disable-next-line @web-platform/custom-linting/use-saddlebag-browser
  const translations = window.__internal?.banana?.translations;

  // Log a warning if translations are not found, which means the whole translation js chunk load failed
  if (!translations) {
    logWarn({
      component: 'getTranslations',
      message: 'Error getting translations',
    });
  }

  // Set a new object but not reference to translations object.
  const translationCopy: Record<string, string> = { ...translations };
  translationBody = translationCopy;
  hasTranslations = Object.keys(translationCopy).length > 0;

  return translationBody;
};

/**
 * Get the Translated string.
 * @param {string} translationKey - The static string of the translation key, like 'kxtx_key'. See more context: https://github.com/skyscanner/banana/blob/main/docs/03-how-to-guides/translations.md#notes
 * @param {Object} context - The custom parameters which need to be replaced in the translated string.
 * @returns {string} The final translated string.
 */
export const translate = (
  translationKey: TranslationKey,
  context: Record<string, unknown> = {},
): string => {
  const translations = getTranslations();

  // Only log a warning if the translations object exist but the translation key does not exist.
  if (hasTranslations && !translations[translationKey]) {
    logWarn({
      component: 'translate',
      message: `Translation key ${translationKey} not found`,
    });
  }

  let string = translations[translationKey] || translationKey;
  Object.keys(context).forEach((key) => {
    const value = String(context[key]);
    string = string.replace(new RegExp(`@@${key}@@|{${key}}`, 'g'), value);
  });
  return string;
};

/**
 * Given a list of translation keys, return the translated strings for all keys.
 *
 * Does not support placeholder replacement. For placeholders, use the translate() function per string.
 *
 * Designed specifically for GlobalComponents, for GCs that export a list of their translation keys to allow passing them as a blob to the same GC's string mapper function.
 *
 * https://github.com/Skyscanner/web-documentation/blob/ef696684be672a9428a5551d50a60a45f47832da/docs/global-components/why-string-mapper.md
 *
 * @param {string} translationKeys A list of translation keys to be translated.
 * @returns {Record<TranslationKeyOfGC, string>} An object of `translation key: translation value` pairs.
 */
export const translateStrings = <T extends TranslationKeyOfGC>(translationKeys: readonly T[]) => {
  const translations = getTranslations();
  // @ts-expect-error: set the type of translations key to be the same as the translationKeys
  const strings: Record<T, string> = {};
  translationKeys.forEach((key) => {
    strings[key] = translations[key] || key;
    // Only log a warning if the translations object exist but the translation key does not exist.
    if (hasTranslations && !translations[key]) {
      logWarn({
        component: 'translateStrings',
        message: `Translation key ${key} not found`,
      });
    }
  });
  return strings;
};
